import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye, faFileArrowUp, faFilePdf, faShieldHalved, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import {
    delete_audit_document,
    export_audit_documents,
    fetch_audit_documents,
    fetch_audit_gate_check_pdf,
    fetch_audit_gate_checks,
    get_audit_document_url,
    upload_audit_document,
    verify_audit_document,
} from '../../api/service';
import { getAuditInfringements } from '../../api/tacho';
import { useAlert } from '../../providers/AlertProvider';
import '../css/TransportAuditDocuments.css';

const DOCUMENT_TYPES = {
    missing_mileage: 'Missing Mileage Report',
    monthly_defects: 'Monthly Defect Report',
    infringement_assessment: 'Infringement Assessment',
    maintenance_inspection: 'Maintenance Inspection',
    brake_test: 'Brake Performance Test',
    tacho_calibration: 'Tachograph Calibration',
    driver_training: 'Driver Training Evidence',
    licence_check: 'Driving Licence Check',
    internal_audit: 'Internal Transport Audit',
    other: 'Other Audit Evidence',
};

const ASSESSMENT_OUTCOMES = {
    satisfactory: 'Satisfactory',
    action_required: 'Action Required',
    escalated: 'Escalated',
};
const MONTHLY_DOCUMENT_TYPES = new Set(['missing_mileage', 'monthly_defects']);
const GATE_CHECK_OUTCOMES = {
    confirmed: 'Confirmed',
    discrepancy: 'Discrepancy',
    additional_defect: 'Additional Defect',
};

const today = new Date();
const currentYear = today.getFullYear();
const currentMonthNumber = today.getMonth() + 1;
const currentMonth = `${currentYear}-${String(currentMonthNumber).padStart(2, '0')}`;
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
    value: String(index + 1).padStart(2, '0'),
    label: new Date(2000, index, 1).toLocaleDateString('en-GB', { month: 'long' }),
}));
const REPORT_YEAR_OPTIONS = Array.from({ length: currentYear - 2009 }, (_, index) => String(currentYear - index));

const monthLabel = (value) => new Date(`${value.slice(0, 7)}-01T00:00:00`)
    .toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

const formatBytes = (bytes) => {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TransportAuditDocuments = ({ theme }) => {
    const { showAlert } = useAlert();
    const fileInputRef = useRef(null);
    const [documents, setDocuments] = useState([]);
    const [gateChecks, setGateChecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gateChecksLoading, setGateChecksLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [documentType, setDocumentType] = useState('missing_mileage');
    const [reportMonthNumber, setReportMonthNumber] = useState(String(currentMonthNumber).padStart(2, '0'));
    const [reportYear, setReportYear] = useState(String(currentYear));
    const [notes, setNotes] = useState('');
    const [file, setFile] = useState(null);
    const [infringements, setInfringements] = useState([]);
    const [infringementsLoading, setInfringementsLoading] = useState(false);
    const [tachoInfringementId, setTachoInfringementId] = useState('');
    const [evidenceDate, setEvidenceDate] = useState('');
    const [assessmentOutcome, setAssessmentOutcome] = useState('satisfactory');
    const [typeFilter, setTypeFilter] = useState('all');
    const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
    const [verifyingIds, setVerifyingIds] = useState([]);
    const [verificationResults, setVerificationResults] = useState({});
    const [generatingGateCheckIds, setGeneratingGateCheckIds] = useState([]);
    const [gateCheckStartMonth, setGateCheckStartMonth] = useState(currentMonth);
    const [gateCheckEndMonth, setGateCheckEndMonth] = useState(currentMonth);
    const [exportStartMonth, setExportStartMonth] = useState(`${currentYear}-01`);
    const [exportEndMonth, setExportEndMonth] = useState(currentMonth);
    const [exporting, setExporting] = useState(false);
    const reportMonth = `${reportYear}-${reportMonthNumber}`;

    const handleReportYearChange = (event) => {
        const nextYear = event.target.value;
        setReportYear(nextYear);
        if (Number(nextYear) === currentYear && Number(reportMonthNumber) > currentMonthNumber) {
            setReportMonthNumber(String(currentMonthNumber).padStart(2, '0'));
        }
    };

    const updateGateCheckStartMonth = (nextMonth) => {
        setGateCheckStartMonth(nextMonth);
        if (nextMonth > gateCheckEndMonth) {
            setGateCheckEndMonth(nextMonth);
        }
    };

    const updateGateCheckEndMonth = (nextMonth) => {
        setGateCheckEndMonth(nextMonth);
        if (nextMonth < gateCheckStartMonth) {
            setGateCheckStartMonth(nextMonth);
        }
    };

    const updateExportStartMonth = (nextMonth) => {
        setExportStartMonth(nextMonth);
        if (nextMonth > exportEndMonth) {
            setExportEndMonth(nextMonth);
        }
    };

    const updateExportEndMonth = (nextMonth) => {
        setExportEndMonth(nextMonth);
        if (nextMonth < exportStartMonth) {
            setExportStartMonth(nextMonth);
        }
    };

    const loadDocuments = async () => {
        setLoading(true);
        const result = await fetch_audit_documents();
        if (result.status === 200) {
            setDocuments(result.data || []);
        } else {
            showAlert({ message: result.data?.error || 'Failed to load audit documents', type: 'danger', buttons: [{ label: 'Close' }] });
        }
        setLoading(false);
    };

    const loadGateChecks = async () => {
        if (gateCheckStartMonth > gateCheckEndMonth) {
            showAlert({
                message: 'The gate-check start month cannot be after the end month.',
                type: 'danger',
                buttons: [{ label: 'Close' }],
            });
            return;
        }
        setGateChecksLoading(true);
        const result = await fetch_audit_gate_checks(gateCheckStartMonth, gateCheckEndMonth);
        if (result.status === 200) {
            setGateChecks(result.data || []);
        } else {
            showAlert({
                message: result.data?.error || 'Failed to load gate-check evidence',
                type: 'danger',
                buttons: [{ label: 'Close' }],
            });
        }
        setGateChecksLoading(false);
    };

    useEffect(() => {
        loadDocuments();
        loadGateChecks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (documentType !== 'infringement_assessment' || infringements.length > 0) return;

        const loadInfringements = async () => {
            setInfringementsLoading(true);
            const result = await getAuditInfringements();
            setInfringementsLoading(false);
            if (!result.success) {
                showAlert({
                    message: result.error || 'Failed to load tacho infringements',
                    type: 'danger',
                    buttons: [{ label: 'Close' }],
                });
                return;
            }
            setInfringements(result.infringements);
        };

        loadInfringements();
    }, [documentType, infringements.length, showAlert]);

    const years = useMemo(() => {
        const values = new Set(documents.map(document => document.report_month?.slice(0, 4)).filter(Boolean));
        values.add(String(new Date().getFullYear()));
        return [...values].sort((a, b) => b.localeCompare(a));
    }, [documents]);

    const filteredDocuments = useMemo(() => documents.filter(document => (
        (typeFilter === 'all' || document.document_type === typeFilter)
        && (yearFilter === 'all' || document.report_month?.startsWith(yearFilter))
    )), [documents, typeFilter, yearFilter]);

    const missingReports = useMemo(() => {
        const present = new Set(documents.map(document => `${document.document_type}:${document.report_month?.slice(0, 7)}`));
        const missing = [];
        const cursor = new Date();
        cursor.setDate(1);
        for (let offset = 0; offset < 12; offset += 1) {
            const month = cursor.toISOString().slice(0, 7);
            ['missing_mileage', 'monthly_defects'].forEach(type => {
                if (!present.has(`${type}:${month}`)) missing.push({ type, month });
            });
            cursor.setMonth(cursor.getMonth() - 1);
        }
        return missing;
    }, [documents]);

    const handleFileChange = (event) => {
        const selected = event.target.files?.[0] || null;
        if (selected && (selected.type !== 'application/pdf' || !selected.name.toLowerCase().endsWith('.pdf'))) {
            showAlert({ message: 'Please select a PDF file.', type: 'danger', buttons: [{ label: 'Close' }] });
            event.target.value = '';
            return;
        }
        if (selected && selected.size > 20 * 1024 * 1024) {
            showAlert({ message: 'PDF files must be 20 MB or smaller.', type: 'danger', buttons: [{ label: 'Close' }] });
            event.target.value = '';
            return;
        }
        setFile(selected);
    };

    const handleUpload = async (event) => {
        event.preventDefault();
        if (!file || !reportMonth) return;
        if (!MONTHLY_DOCUMENT_TYPES.has(documentType) && !evidenceDate) {
            showAlert({
                message: 'Select the date covered by this evidence.',
                type: 'danger',
                buttons: [{ label: 'Close' }],
            });
            return;
        }
        if (documentType === 'infringement_assessment' && (!tachoInfringementId || !assessmentOutcome)) {
            showAlert({
                message: 'Select an infringement, evidence date, and assessment outcome.',
                type: 'danger',
                buttons: [{ label: 'Close' }],
            });
            return;
        }
        setUploading(true);
        const result = await upload_audit_document({
            file,
            documentType,
            reportMonth,
            notes: notes.trim(),
            evidenceDate: MONTHLY_DOCUMENT_TYPES.has(documentType) ? null : evidenceDate,
            tachoInfringementId: documentType === 'infringement_assessment' ? tachoInfringementId : null,
            assessmentOutcome: documentType === 'infringement_assessment' ? assessmentOutcome : null,
        });
        setUploading(false);
        if (result.status !== 201) {
            showAlert({ message: result.data?.error || 'Failed to upload audit document', type: 'danger', buttons: [{ label: 'Close' }] });
            return;
        }
        setFile(null);
        setNotes('');
        setTachoInfringementId('');
        setEvidenceDate('');
        setAssessmentOutcome('satisfactory');
        if (fileInputRef.current) fileInputRef.current.value = '';
        await loadDocuments();
        showAlert({ message: 'Audit document uploaded successfully.', type: 'success', buttons: [{ label: 'Close' }] });
    };

    const getDocumentUrl = async (documentId) => {
        const result = await get_audit_document_url(documentId);
        if (result.status !== 200) {
            showAlert({ message: result.data?.error || 'Failed to open audit document', type: 'danger', buttons: [{ label: 'Close' }] });
            return null;
        }
        return result.data.signed_url;
    };

    const verifyDocument = async (documentId, showSuccess = false) => {
        setVerifyingIds(current => [...current, documentId]);
        const result = await verify_audit_document(documentId);
        setVerifyingIds(current => current.filter(id => id !== documentId));

        if (result.status !== 200) {
            setVerificationResults(current => ({ ...current, [documentId]: 'error' }));
            showAlert({ message: result.data?.error || 'Failed to verify audit document', type: 'danger', buttons: [{ label: 'Close' }] });
            return false;
        }

        const verificationStatus = result.data.verified ? 'verified' : 'failed';
        setVerificationResults(current => ({ ...current, [documentId]: verificationStatus }));
        if (!result.data.verified) {
            showAlert({ message: 'Integrity check failed. This document does not match the file originally uploaded.', type: 'danger', buttons: [{ label: 'Close' }] });
            return false;
        }
        if (showSuccess) {
            showAlert({ message: 'Integrity verified. The stored PDF matches the original upload.', type: 'success', buttons: [{ label: 'Close' }] });
        }
        return true;
    };

    const handlePreview = async (documentId) => {
        const previewWindow = window.open('', '_blank');
        const verified = await verifyDocument(documentId);
        if (!verified) {
            if (previewWindow) previewWindow.close();
            return;
        }
        const url = await getDocumentUrl(documentId);
        if (url && previewWindow) previewWindow.location = url;
        else if (previewWindow) previewWindow.close();
    };

    const handleDownload = async (document) => {
        const verified = await verifyDocument(document.id);
        if (!verified) return;
        const url = await getDocumentUrl(document.id);
        if (!url) return;
        const anchor = window.document.createElement('a');
        anchor.href = url;
        anchor.download = document.original_filename;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.click();
    };

    const removeDocument = async (document) => {
        const result = await delete_audit_document(document.id);
        if (result.status !== 200) {
            showAlert({ message: result.data?.error || 'Failed to remove audit document', type: 'danger', buttons: [{ label: 'Close' }] });
            return;
        }
        await loadDocuments();
    };

    const confirmDelete = (document) => showAlert({
        message: `Remove ${document.original_filename} from the active audit register? The retained evidence file will not be destroyed.`,
        type: 'danger',
        buttons: [
            { label: 'Remove', type: 'danger', onClick: () => removeDocument(document) },
            { label: 'Cancel' },
        ],
    });

    const handleExport = async () => {
        setExporting(true);
        const result = await export_audit_documents(exportStartMonth, exportEndMonth);
        setExporting(false);
        if (result.status !== 200) {
            showAlert({
                message: result.data?.error || 'Failed to export audit documents',
                type: 'danger',
                buttons: [{ label: 'Close' }],
            });
            return;
        }

        const url = window.URL.createObjectURL(result.blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = result.filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
    };

    const openGateCheckPdf = async (gateCheck, download) => {
        const previewWindow = download ? null : window.open('', '_blank');
        setGeneratingGateCheckIds(current => [...current, gateCheck.id]);
        const result = await fetch_audit_gate_check_pdf(gateCheck.id);
        setGeneratingGateCheckIds(current => current.filter(id => id !== gateCheck.id));

        if (result.status !== 200) {
            if (previewWindow) previewWindow.close();
            showAlert({
                message: result.data?.error || 'Failed to generate gate-check PDF',
                type: 'danger',
                buttons: [{ label: 'Close' }],
            });
            return;
        }

        const url = window.URL.createObjectURL(result.blob);
        if (download) {
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = result.filename;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(url);
            return;
        }

        if (previewWindow) {
            previewWindow.location = url;
            window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        }
    };

    return (
        <div id={theme} className="audit-documents">
            <header className="audit-header">
                <div>
                    <h2>Transport Audit Documents</h2>
                    <p>Monthly compliance evidence retained with uploader and checksum details.</p>
                </div>
                <div className={`audit-coverage ${missingReports.length ? 'has-gaps' : ''}`}>
                    <strong>{missingReports.length ? `${missingReports.length} missing` : 'Complete'}</strong>
                    <span>Last 12 months</span>
                </div>
            </header>

            <section className="audit-export">
                <div>
                    <h3>Download Review Pack</h3>
                    <p>Creates a ZIP containing every audit PDF in the selected reporting months and an overview PDF.</p>
                </div>
                <label>
                    <span>From</span>
                    <span className="audit-gate-month-picker">
                        <select
                            aria-label="Review pack start month"
                            value={exportStartMonth.slice(5, 7)}
                            onChange={event => updateExportStartMonth(
                                `${exportStartMonth.slice(0, 4)}-${event.target.value}`
                            )}
                        >
                            {MONTH_OPTIONS.map(month => (
                                <option
                                    key={month.value}
                                    value={month.value}
                                    disabled={
                                        Number(exportStartMonth.slice(0, 4)) === currentYear
                                        && Number(month.value) > currentMonthNumber
                                    }
                                >
                                    {month.label}
                                </option>
                            ))}
                        </select>
                        <select
                            aria-label="Review pack start year"
                            value={exportStartMonth.slice(0, 4)}
                            onChange={event => {
                                const year = event.target.value;
                                const month = Number(year) === currentYear
                                    && Number(exportStartMonth.slice(5, 7)) > currentMonthNumber
                                    ? String(currentMonthNumber).padStart(2, '0')
                                    : exportStartMonth.slice(5, 7);
                                updateExportStartMonth(`${year}-${month}`);
                            }}
                        >
                            {REPORT_YEAR_OPTIONS.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </span>
                </label>
                <label>
                    <span>To</span>
                    <span className="audit-gate-month-picker">
                        <select
                            aria-label="Review pack end month"
                            value={exportEndMonth.slice(5, 7)}
                            onChange={event => updateExportEndMonth(
                                `${exportEndMonth.slice(0, 4)}-${event.target.value}`
                            )}
                        >
                            {MONTH_OPTIONS.map(month => (
                                <option
                                    key={month.value}
                                    value={month.value}
                                    disabled={
                                        Number(exportEndMonth.slice(0, 4)) === currentYear
                                        && Number(month.value) > currentMonthNumber
                                    }
                                >
                                    {month.label}
                                </option>
                            ))}
                        </select>
                        <select
                            aria-label="Review pack end year"
                            value={exportEndMonth.slice(0, 4)}
                            onChange={event => {
                                const year = event.target.value;
                                const month = Number(year) === currentYear
                                    && Number(exportEndMonth.slice(5, 7)) > currentMonthNumber
                                    ? String(currentMonthNumber).padStart(2, '0')
                                    : exportEndMonth.slice(5, 7);
                                updateExportEndMonth(`${year}-${month}`);
                            }}
                        >
                            {REPORT_YEAR_OPTIONS.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </span>
                </label>
                <button
                    type="button"
                    className="audit-export-button"
                    disabled={exporting}
                    onClick={handleExport}
                >
                    <FontAwesomeIcon icon={faDownload} style={{ width: 14 }} />
                    {exporting ? 'Creating ZIP...' : 'Download ZIP'}
                </button>
            </section>

            <div className="audit-layout">
                <form className="audit-upload" onSubmit={handleUpload}>
                    <h3>Upload Evidence</h3>
                    <label>Document type</label>
                    <select value={documentType} onChange={event => setDocumentType(event.target.value)}>
                        {Object.entries(DOCUMENT_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    {documentType === 'infringement_assessment' && (
                        <>
                            <label>Tacho infringement</label>
                            <select
                                value={tachoInfringementId}
                                disabled={infringementsLoading}
                                onChange={event => {
                                    const id = event.target.value;
                                    setTachoInfringementId(id);
                                    const infringement = infringements.find(item => String(item.id) === id);
                                    setEvidenceDate(infringement?.infringement_date?.slice(0, 10) || '');
                                }}
                                required
                            >
                                <option value="">{infringementsLoading ? 'Loading infringements...' : 'Select infringement'}</option>
                                {infringements.map(infringement => (
                                    <option key={infringement.id} value={infringement.id}>
                                        {infringement.driver_name} {infringement.driver_surname} · {infringement.infringement_date} · {infringement.infringement_type?.replaceAll('_', ' ')}
                                    </option>
                                ))}
                            </select>
                            <label>Assessment outcome</label>
                            <select value={assessmentOutcome} onChange={event => setAssessmentOutcome(event.target.value)}>
                                {Object.entries(ASSESSMENT_OUTCOMES).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </>
                    )}
                    {!MONTHLY_DOCUMENT_TYPES.has(documentType) && (
                        <>
                            <label>Evidence date</label>
                            <input type="date" value={evidenceDate} onChange={event => setEvidenceDate(event.target.value)} required />
                        </>
                    )}
                    <label>Reporting month</label>
                    <div className="audit-month-fields">
                        <select aria-label="Reporting month" value={reportMonthNumber} onChange={event => setReportMonthNumber(event.target.value)}>
                            {MONTH_OPTIONS.map(month => (
                                <option
                                    key={month.value}
                                    value={month.value}
                                    disabled={Number(reportYear) === currentYear && Number(month.value) > currentMonthNumber}
                                >
                                    {month.label}
                                </option>
                            ))}
                        </select>
                        <select aria-label="Reporting year" value={reportYear} onChange={handleReportYearChange}>
                            {REPORT_YEAR_OPTIONS.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </div>
                    <label>PDF document</label>
                    <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" onChange={handleFileChange} required />
                    <label>Notes</label>
                    <textarea value={notes} maxLength={500} onChange={event => setNotes(event.target.value)} rows={3} placeholder="Optional audit context" />
                    <button type="submit" className="audit-primary-button" disabled={!file || uploading}>
                        <FontAwesomeIcon icon={faFileArrowUp} style={{ width: 14 }} /> {uploading ? 'Uploading...' : 'Upload PDF'}
                    </button>
                </form>

                <section className="audit-register">
                    <div className="audit-toolbar">
                        <h3>Document Register</h3>
                        <select aria-label="Filter by document type" value={typeFilter} onChange={event => setTypeFilter(event.target.value)}>
                            <option value="all">All document types</option>
                            {Object.entries(DOCUMENT_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                        <select aria-label="Filter by year" value={yearFilter} onChange={event => setYearFilter(event.target.value)}>
                            <option value="all">All years</option>
                            {years.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </div>

                    <div className="audit-table-wrap">
                        <table className="audit-table">
                            <thead><tr><th>Month</th><th>Type</th><th>Document</th><th>Uploaded</th><th>Actions</th></tr></thead>
                            <tbody>
                                {!loading && filteredDocuments.map(document => (
                                    <tr key={document.id}>
                                        <td>{monthLabel(document.report_month)}</td>
                                        <td><span className={`audit-type audit-type-${document.document_type}`}>{DOCUMENT_TYPES[document.document_type]}</span></td>
                                        <td>
                                            <strong><FontAwesomeIcon icon={faFilePdf} style={{ width: 14 }} /> {document.original_filename}</strong>
                                            <small title={`SHA-256: ${document.checksum_sha256}`}>{formatBytes(document.file_size)} · Checksum {document.checksum_sha256?.slice(0, 10)}...</small>
                                            {verificationResults[document.id] === 'verified' && <small className="audit-integrity verified">Integrity verified</small>}
                                            {['failed', 'error'].includes(verificationResults[document.id]) && <small className="audit-integrity failed">Integrity check failed</small>}
                                            {document.notes && <small>{document.notes}</small>}
                                            {document.evidence_date && <small>Evidence date: {new Date(`${document.evidence_date}T00:00:00`).toLocaleDateString('en-GB')}</small>}
                                            {document.tacho_infringements && (
                                                <small>
                                                    {document.tacho_infringements.driver_name} {document.tacho_infringements.driver_surname}
                                                    {document.tacho_infringements.vehicle_registration ? ` · ${document.tacho_infringements.vehicle_registration}` : ''}
                                                    {document.assessment_outcome ? ` · ${ASSESSMENT_OUTCOMES[document.assessment_outcome]}` : ''}
                                                </small>
                                            )}
                                        </td>
                                        <td>{new Date(document.created_at).toLocaleDateString('en-GB')}<small>{document.uploaded_by_name || 'Recorded user'}</small></td>
                                        <td>
                                            <div className="audit-actions">
                                                <button type="button" title="Verify integrity" disabled={verifyingIds.includes(document.id)} onClick={() => verifyDocument(document.id, true)}><FontAwesomeIcon icon={faShieldHalved} style={{ width: 14 }} /></button>
                                                <button type="button" title="Preview PDF" disabled={verifyingIds.includes(document.id)} onClick={() => handlePreview(document.id)}><FontAwesomeIcon icon={faEye} style={{ width: 14 }} /></button>
                                                <button type="button" title="Download PDF" disabled={verifyingIds.includes(document.id)} onClick={() => handleDownload(document)}><FontAwesomeIcon icon={faDownload} style={{ width: 14 }} /></button>
                                                <button type="button" title="Remove from register" className="danger" onClick={() => confirmDelete(document)}><FontAwesomeIcon icon={faTrashCan} style={{ width: 14 }} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && filteredDocuments.length === 0 && <tr><td colSpan="5" className="audit-empty">No audit documents match these filters.</td></tr>}
                                {loading && <tr><td colSpan="5" className="audit-empty">Loading audit documents...</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <div className="audit-layout audit-gate-layout">
                <div className="audit-gate-spacer" aria-hidden="true" />
                <section className="audit-register audit-gate-register">
                    <div className="audit-toolbar">
                        <div className="audit-gate-heading">
                            <h3>Gate Check Evidence</h3>
                            <small>Immutable driver and management comparisons</small>
                        </div>
                        <div className="audit-gate-date-fields">
                        <label>
                            <span className="audit-gate-date-label">From</span>
                            <span className="audit-gate-month-picker">
                                <select
                                    aria-label="Gate check start month"
                                    value={gateCheckStartMonth.slice(5, 7)}
                                    onChange={event => updateGateCheckStartMonth(
                                        `${gateCheckStartMonth.slice(0, 4)}-${event.target.value}`
                                    )}
                                >
                                    {MONTH_OPTIONS.map(month => (
                                        <option
                                            key={month.value}
                                            value={month.value}
                                            disabled={
                                                Number(gateCheckStartMonth.slice(0, 4)) === currentYear
                                                && Number(month.value) > currentMonthNumber
                                            }
                                        >
                                            {month.label}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    aria-label="Gate check start year"
                                    value={gateCheckStartMonth.slice(0, 4)}
                                    onChange={event => {
                                        const year = event.target.value;
                                        const month = Number(year) === currentYear
                                            && Number(gateCheckStartMonth.slice(5, 7)) > currentMonthNumber
                                            ? String(currentMonthNumber).padStart(2, '0')
                                            : gateCheckStartMonth.slice(5, 7);
                                        updateGateCheckStartMonth(`${year}-${month}`);
                                    }}
                                >
                                    {REPORT_YEAR_OPTIONS.map(year => <option key={year} value={year}>{year}</option>)}
                                </select>
                            </span>
                        </label>
                        <span className="audit-gate-range-arrow" aria-hidden="true">→</span>
                        <label>
                            <span className="audit-gate-date-label">To</span>
                            <span className="audit-gate-month-picker">
                                <select
                                    aria-label="Gate check end month"
                                    value={gateCheckEndMonth.slice(5, 7)}
                                    onChange={event => updateGateCheckEndMonth(
                                        `${gateCheckEndMonth.slice(0, 4)}-${event.target.value}`
                                    )}
                                >
                                    {MONTH_OPTIONS.map(month => (
                                        <option
                                            key={month.value}
                                            value={month.value}
                                            disabled={
                                                Number(gateCheckEndMonth.slice(0, 4)) === currentYear
                                                && Number(month.value) > currentMonthNumber
                                            }
                                        >
                                            {month.label}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    aria-label="Gate check end year"
                                    value={gateCheckEndMonth.slice(0, 4)}
                                    onChange={event => {
                                        const year = event.target.value;
                                        const month = Number(year) === currentYear
                                            && Number(gateCheckEndMonth.slice(5, 7)) > currentMonthNumber
                                            ? String(currentMonthNumber).padStart(2, '0')
                                            : gateCheckEndMonth.slice(5, 7);
                                        updateGateCheckEndMonth(`${year}-${month}`);
                                    }}
                                >
                                    {REPORT_YEAR_OPTIONS.map(year => <option key={year} value={year}>{year}</option>)}
                                </select>
                            </span>
                        </label>
                        </div>
                        <button type="button" className="audit-refresh-button" onClick={loadGateChecks} disabled={gateChecksLoading}>
                            {gateChecksLoading ? 'Loading...' : 'Search'}
                        </button>
                    </div>
                    <div className="audit-table-wrap">
                        <table className="audit-table audit-gate-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Vehicle</th>
                                <th>Driver</th>
                                <th>Checked by</th>
                                <th>Outcome</th>
                                <th>Differences</th>
                                <th>Evidence</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!gateChecksLoading && gateChecks.map(gateCheck => {
                                const source = gateCheck.source_snapshot || {};
                                const isGenerating = generatingGateCheckIds.includes(gateCheck.id);
                                return (
                                    <tr key={gateCheck.id}>
                                        <td>
                                            {source.date
                                                ? new Date(`${source.date}T00:00:00`).toLocaleDateString('en-GB')
                                                : new Date(gateCheck.submitted_at).toLocaleDateString('en-GB')}
                                            <small>{new Date(gateCheck.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</small>
                                        </td>
                                        <td>
                                            <strong>{gateCheck.vehicle_reg}</strong>
                                            <small>{gateCheck.vehicle_type}</small>
                                        </td>
                                        <td>{gateCheck.driver_name}</td>
                                        <td>{gateCheck.checked_by_name}</td>
                                        <td>
                                            <span className={`audit-gate-outcome ${gateCheck.outcome}`}>
                                                {GATE_CHECK_OUTCOMES[gateCheck.outcome] || gateCheck.outcome}
                                            </span>
                                        </td>
                                        <td>{gateCheck.discrepancy_count}</td>
                                        <td>
                                            <small title={`SHA-256: ${gateCheck.checksum_sha256}`}>
                                                Checksum {gateCheck.checksum_sha256?.slice(0, 10)}...
                                            </small>
                                            <div className="audit-actions">
                                                <button
                                                    type="button"
                                                    title="View comparison PDF"
                                                    disabled={isGenerating}
                                                    onClick={() => openGateCheckPdf(gateCheck, false)}
                                                >
                                                    <FontAwesomeIcon icon={faEye} style={{ width: 14 }} />
                                                </button>
                                                <button
                                                    type="button"
                                                    title="Download comparison PDF"
                                                    disabled={isGenerating}
                                                    onClick={() => openGateCheckPdf(gateCheck, true)}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} style={{ width: 14 }} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!gateChecksLoading && gateChecks.length === 0 && (
                                <tr><td colSpan="7" className="audit-empty">No gate checks were submitted in the selected month range.</td></tr>
                            )}
                            {gateChecksLoading && (
                                <tr><td colSpan="7" className="audit-empty">Loading gate-check evidence...</td></tr>
                            )}
                        </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default TransportAuditDocuments;